import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import LocalAtmIcon from '@mui/icons-material/LocalAtm'
import ListAltIcon from '@mui/icons-material/ListAlt'
import FlutterDashIcon from '@mui/icons-material/FlutterDash'
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation'
import PsychologyAltIcon from '@mui/icons-material/PsychologyAlt'
import AddIcon from '@mui/icons-material/Add'
import { useAuth } from "./api/authStore"
import { loginUrl } from './config/config'
import PaymentDialog from './PaymentDialog'
import ThemeToggle from './components/ThemeToggle'
import HelpModal from './components/HelpModal'
import logo from './img/logo.svg'
import landingLogo from './img/landing-logo.png'

// Компонент навигационного элемента
const NavItem = ({ icon: Icon, label, href, isActive = false, onClick }) => {
    const sharedClass = onClick
        ? 'left-side-menu__nav-button'
        : `left-side-menu__nav-link${isActive ? ' left-side-menu__nav-link--active gold-gradient-border' : ''}`

    const content = (
        <>
            <Icon style={{ 
                fontSize: '20px', 
                marginRight: 'var(--spacing-md)',
                fill: 'url(#sidebarGoldGradient)'
            }} />
            <span className="left-side-menu__nav-label">
                {label}
            </span>
        </>
    )

    if (onClick) {
        return (
            <button onClick={onClick} className={sharedClass}>
                {content}
            </button>
        )
    }
    
    return (
        <Link to={href} className={sharedClass}>
            {content}
        </Link>
    )
}

// Компонент аватара с пульсирующим лого
const UserAvatar = ({ email }) => {
    return (
        <div style={{
            width: '48px',
            height: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: 'var(--spacing-md)',
            position: 'relative'
        }}>
            <div
                role="img"
                aria-label="XSocks Logo"
                style={{
                    width: '40px',
                    height: '40px',
                    background: 'var(--gradient-orange)',
                    WebkitMaskImage: `url(${landingLogo})`,
                    maskImage: `url(${landingLogo})`,
                    WebkitMaskRepeat: 'no-repeat',
                    maskRepeat: 'no-repeat',
                    WebkitMaskPosition: 'center',
                    maskPosition: 'center',
                    WebkitMaskSize: 'contain',
                    maskSize: 'contain',
                    filter: 'var(--sidebar-logo-glow)',
                    animation: 'pulse 2s ease-in-out infinite'
                }}
            />
            <style>{`
                @keyframes pulse {
                    0%, 100% {
                        transform: scale(1);
                        opacity: 1;
                    }
                    50% {
                        transform: scale(1.1);
                        opacity: 0.8;
                    }
                }
            `}</style>
        </div>
    )
}

const LeftSideMenu = React.memo((props) => {
    const { getAccessTokenSilently, setToken, logout } = useAuth()
    const [openPaymentDialog, setOpentPaymentDialog] = React.useState(false)
    const [showHelpModal, setShowHelpModal] = React.useState(false)
    const location = useLocation()
    
    // Используем props.user вместо useAuth().user для консистентности
    const user = props.user
    const accountEmail = user?.username || ''
    const balance = user?.balance ? (user.balance/100).toFixed(2) : '0.00'
    const activeProxiesList = props.active?.rentals || []
    const activeProxiesCount = activeProxiesList.length

    // Логирование только в режиме разработки
    React.useEffect(() => {
        if (user && process.env.NODE_ENV === 'development') {
        }
    }, [user])

    const handleLogout = () => {
        logout(loginUrl)
    }

    const handleHelpClick = (e) => {
        e.preventDefault()
        setShowHelpModal(true)
    }

    const handleOpenPaymentDialog = () => {
        setOpentPaymentDialog(true)
    }

    // Определяем активную страницу
    const currentPath = location.pathname

    return (
        <div className="left-side-menu">
            {/* Заголовок */}
            <div className="left-side-menu__header">
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <h1 className="left-side-menu__brand">
                        {process.env.REACT_APP_NAME}
                    </h1>
                </div>
            </div>

            {/* Профиль пользователя */}
            <div className="left-side-menu__profile">
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: 'var(--spacing-md)'
                }}>
                    <UserAvatar email={accountEmail} />
                    <div style={{ flex: 1 }}>
                        <div className="left-side-menu__email">
                            <Link to='/account'>
                                {accountEmail.replace(/(.{20})..+/, "$1…")}
                            </Link>
                        </div>
                        <div className="left-side-menu__balance">
                            <span className="left-side-menu__balance-label">
                                Balance: <strong className="left-side-menu__balance-value">${balance}</strong>
                            </span>
                            <button
                                onClick={handleOpenPaymentDialog}
                                title="Add Funds"
                                aria-label="Add Funds"
                                style={{
                                    width: '22px',
                                    height: '22px',
                                    borderRadius: '50%',
                                    background: 'var(--gradient-orange)',
                                    border: 'none',
                                    padding: 0,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    flexShrink: 0
                                }}
                            >
                                <AddIcon style={{ fontSize: '14px', color: '#1a1a1a' }} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Навигационное меню */}
            <div className="left-side-menu__nav">
                <NavItem 
                    icon={LocalGasStationIcon} 
                    label="Proxies Store" 
                    href="/proxies"
                    isActive={currentPath === '/proxies'}
                />
                
                <NavItem 
                    icon={FlutterDashIcon} 
                    label={`Active Proxies${activeProxiesCount > 0 ? ` (${activeProxiesCount})` : ''}`} 
                    href="/active"
                    isActive={currentPath === '/active'}
                />
                
                <NavItem 
                    icon={AccessTimeIcon} 
                    label="History" 
                    href="/history"
                    isActive={currentPath === '/history'}
                />
                
                <NavItem 
                    icon={LocalAtmIcon} 
                    label="Transactions" 
                    href="/transactions"
                    isActive={currentPath === '/transactions'}
                />
                
                <NavItem 
                    icon={ListAltIcon} 
                    label="Tickets" 
                    href="/tickets"
                    isActive={currentPath === '/tickets'}
                />
            </div>

            {/* Нижняя секция */}
            <div className="left-side-menu__footer">
                <ThemeToggle />
                
                <NavItem 
                    icon={PsychologyAltIcon} 
                    label="Help" 
                    onClick={handleHelpClick}
                />
                
                <NavItem 
                    icon={PowerSettingsNewIcon} 
                    label="Logout" 
                    onClick={handleLogout}
                />
            </div>

            <PaymentDialog 
                open={openPaymentDialog} 
                setOpentPaymentDialog={setOpentPaymentDialog} 
                apiClient={props.apiClient}
            />
            
            <HelpModal 
                isOpen={showHelpModal} 
                onClose={() => setShowHelpModal(false)}
            />
        </div> 
    )
})

export default LeftSideMenu